import { Alert, App as AntApp, Button, Drawer, Empty, Input, Popconfirm, Result, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  ArrowRightIcon,
  BedDoubleIcon,
  CalendarDaysIcon,
  HistoryIcon,
  MapPinIcon,
  PlusIcon,
  SendIcon,
  SparklesIcon,
  Trash2Icon,
  UsersRoundIcon,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  assistantKeys,
  AssistantChatError,
  sendPortalAiMessage,
  useDeletePortalAiThreadMutation,
  usePortalAiStatusQuery,
  usePortalAiThreadsQuery,
  type AssistantDraft,
  type AssistantMessage,
  type AssistantThread,
} from "../actions/assistant.actions";
import { useSavePortalItineraryDraftMutation } from "../actions/itinerary.actions";
import { getPortalPaths } from "../lib/routes";
import { portalRequest } from "../lib/auth";

const SUGGESTIONS = [
  "Honeymoon couple, 6 nights, Hanoi – Ha Long – Hoi An, 4★ hotels",
  "Family of 4, 7 nights, vegetarian meals, relaxed pace",
  "Group of 20 adults, 4 nights, beach break",
];

const FOLLOW_UPS = ["Make it 5★", "Cheaper options", "Add one more night", "Swap the last hotel"];

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
};

const formatDay = (value: string) => dayjs(value).format("ddd, D MMM");

/** Cross-fading product photos shown while the assistant searches, instead of plain waiting text. */
const LoadingImageCarousel = ({ images }: { images: string[] }) => {
  const [index, setIndex] = useState(0);
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (images.length < 2 || reduceMotion) return;
    const timer = window.setInterval(() => setIndex((current) => current + 1), 2200);
    return () => window.clearInterval(timer);
  }, [images.length, reduceMotion]);

  if (!images.length) return null;
  const active = index % images.length;
  return (
    <div className="relative mt-2 h-24 w-44 overflow-hidden rounded-lg border border-gray-200 bg-gray-100" aria-hidden="true">
      {images.map((src, imageIndex) => (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            imageIndex === active ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
};

/** Renders assistant text with simple **bold** and "- " bullet support, without injecting HTML. */
const MessageText = ({ text }: { text: string }) => {
  const lines = text.split("\n");
  const renderInline = (line: string): ReactNode[] =>
    line.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : (
        <Fragment key={index}>{part}</Fragment>
      ),
    );
  return (
    <div className="space-y-2 text-sm leading-6">
      {lines.map((line, index) => {
        const heading = /^\s*(#{1,4})\s+(.*)$/.exec(line);
        if (heading) {
          return heading[1].length <= 2 ? (
            <h3 key={index} className="!mt-4 border-b border-gray-200 pb-1 text-sm font-semibold tracking-tight text-[#131B2A] first:!mt-0">
              {renderInline(heading[2])}
            </h3>
          ) : (
            <h4 key={index} className="!mt-3 text-sm font-semibold text-[#131B2A]">
              {renderInline(heading[2])}
            </h4>
          );
        }
        const bullet = /^\s*[-•*]\s+(.*)$/.exec(line);
        if (bullet) {
          return (
            <div key={index} className="flex gap-2 pl-1">
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-(--brand-primary)" />
              <span>{renderInline(bullet[1])}</span>
            </div>
          );
        }
        return line.trim() ? <p key={index}>{renderInline(line)}</p> : null;
      })}
    </div>
  );
};

/** Shows the itinerary the assistant is building, with pricing and a hand-off into the builder. */
const DraftPanel = ({
  draft,
  busy,
  saving,
  onSave,
  onFix,
}: {
  draft: AssistantDraft | null;
  busy: boolean;
  saving: boolean;
  onSave: () => void;
  onFix: (prompt: string) => void;
}) => {
  if (!draft) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-[#FBFBFB] text-(--brand-primary)">
          <CalendarDaysIcon size={20} />
        </span>
        <h3 className="mt-3 text-sm font-semibold text-[#131B2A]">Your itinerary will appear here</h3>
        <p className="mt-1 max-w-xs text-xs leading-5 text-gray-500">
          Describe the trip in the chat. The assistant builds a priced draft you can refine, then open in the builder.
        </p>
      </div>
    );
  }

  const { summary } = draft;
  const travellerLabel = [
    `${summary.travellers.adults} adult${summary.travellers.adults === 1 ? "" : "s"}`,
    summary.travellers.cwb ? `${summary.travellers.cwb} child with bed` : "",
    summary.travellers.cnb ? `${summary.travellers.cnb} child no bed` : "",
    summary.travellers.infant ? `${summary.travellers.infant} infant${summary.travellers.infant === 1 ? "" : "s"}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const hasGaps = summary.gaps.daysWithoutItems.length > 0 || summary.gaps.uncoveredHotelNights.length > 0;
  const buildFixPrompt = () => {
    const parts: string[] = [];
    if (summary.gaps.uncoveredHotelNights.length) {
      parts.push(
        `add a hotel for ${summary.gaps.uncoveredHotelNights
          .map((night) => `the night of ${formatDay(dayjs(summary.startDate).add(night, "day").format("YYYY-MM-DD"))}`)
          .join(", ")}`,
      );
    }
    if (summary.gaps.daysWithoutItems.length) {
      parts.push(
        `plan activities for ${summary.gaps.daysWithoutItems
          .map((day) => `Day ${day + 1} (${formatDay(dayjs(summary.startDate).add(day, "day").format("YYYY-MM-DD"))})`)
          .join(", ")}`,
      );
    }
    return `Please complete the itinerary: ${parts.join(" and ")}. Days are counted from Day 1 on the arrival date. Pick suitable options in the right city and keep everything else unchanged.`;
  };

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">Live draft</p>
          <h3 className="mt-1 text-base font-semibold text-[#131B2A]">{summary.title}</h3>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <CalendarDaysIcon size={13} /> {formatDay(summary.startDate)} – {formatDay(summary.endDate)}
            </span>
            <span className="inline-flex items-center gap-1">
              <UsersRoundIcon size={13} /> {travellerLabel}
            </span>
          </div>
        </div>

        {hasGaps ? (
          <Alert
            className="!mb-5"
            type="warning"
            showIcon
            action={
              <Button size="small" type="primary" disabled={busy} onClick={() => onFix(buildFixPrompt())}>
                Fix now
              </Button>
            }
            message="This draft isn't complete yet"
            description={[
              summary.gaps.uncoveredHotelNights.length
                ? `No hotel for night${summary.gaps.uncoveredHotelNights.length > 1 ? "s" : ""} ${summary.gaps.uncoveredHotelNights.map((n) => n + 1).join(", ")}.`
                : "",
              summary.gaps.daysWithoutItems.length
                ? `No activities on day${summary.gaps.daysWithoutItems.length > 1 ? "s" : ""} ${summary.gaps.daysWithoutItems.map((n) => n + 1).join(", ")}.`
                : "",
              "Use Fix now to have the assistant fill them, or complete them in the builder.",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        ) : null}

        <ol className="space-y-3">
          {summary.days.map((day) => {
            const stay = summary.hotels.find((hotel) => hotel.checkInDate === day.date);
            return (
              <li key={day.day} className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-11 shrink-0 items-center justify-center rounded-lg bg-(--brand-secondary) text-[11px] font-semibold text-white">
                  D{day.day + 1}
                </span>
                <div className="min-w-0 flex-1 border-b border-gray-100 pb-3">
                  <p className="text-[11px] text-gray-400">{formatDay(day.date)}</p>
                  {stay ? (
                    <p className="mt-0.5 flex items-start gap-1.5 text-sm font-semibold text-[#131B2A]">
                      <BedDoubleIcon size={14} className="mt-1 shrink-0 text-(--brand-primary)" />
                      <span>
                        {stay.name}
                        <span className="font-normal text-gray-500">
                          {" "}
                          · {stay.city} · {stay.nights}N · {stay.roomType} · {stay.mealPlan}
                        </span>
                      </span>
                    </p>
                  ) : null}
                  {day.items.length ? (
                    <ul className="mt-1 space-y-0.5">
                      {day.items.map((item) => (
                        <li key={`${item.type}-${item.id}`} className="flex items-start gap-1.5 text-xs text-gray-600">
                          <MapPinIcon size={12} className="mt-0.5 shrink-0 text-gray-400" />
                          <span>
                            {item.name}
                            {item.type === "transport" ? <span className="text-gray-400"> · transfer</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : !stay ? (
                    <p className="mt-0.5 text-xs text-gray-400">Nothing planned yet</p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>

        {summary.warnings.length ? (
          <Alert
            type="info"
            showIcon
            message="Pricing notes"
            description={
              <ul className="list-disc pl-4">
                {summary.warnings.slice(0, 4).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            }
          />
        ) : null}
      </div>

      <div className="shrink-0 space-y-3 border-t border-gray-200 bg-[#FBFBFB] p-4 sm:p-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs text-gray-500">Quote total</p>
            <p className="text-xl font-semibold tabular-nums text-[#131B2A]">
              {formatMoney(summary.totalPrice, summary.currency)}
            </p>
          </div>
          <p className="text-right text-xs text-gray-500">
            <span className="block tabular-nums font-semibold text-[#131B2A]">
              {formatMoney(summary.perAdult, summary.currency)}
            </span>
            per adult
          </p>
        </div>
        <Button
          type="primary"
          block
          size="large"
          loading={saving}
          disabled={busy}
          icon={<ArrowRightIcon size={16} />}
          iconPosition="end"
          onClick={onSave}
        >
          Save as trip and open in builder
        </Button>
        <p className="text-center text-[11px] text-gray-400">
          Creates a trip request with this as its first quote. You can keep editing it.
        </p>
      </div>
    </div>
  );
};

/** Lists the agent's earlier AI chats so one can be reopened or deleted. */
const ChatHistoryDrawer = ({
  open,
  activeThreadId,
  onClose,
  onSelect,
  onDeleted,
}: {
  open: boolean;
  activeThreadId?: string;
  onClose: () => void;
  onSelect: (id: string) => void;
  onDeleted: (id: string) => void;
}) => {
  const threads = usePortalAiThreadsQuery(open);
  const remove = useDeletePortalAiThreadMutation();
  const { message: toast } = AntApp.useApp();

  return (
    <Drawer title="Chat history" placement="right" width={360} open={open} onClose={onClose}>
      {threads.isLoading ? <Skeleton active paragraph={{ rows: 6 }} /> : null}
      {threads.isError ? (
        <Alert
          type="error"
          showIcon
          message="Chat history could not be loaded"
          action={
            <Button size="small" onClick={() => void threads.refetch()}>
              Retry
            </Button>
          }
        />
      ) : null}
      {threads.data && !threads.data.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chats yet" />
      ) : null}
      <ul className="-mx-2 space-y-1">
        {(threads.data || []).map((thread) => {
          const isActive = thread.id === activeThreadId;
          return (
            <li key={thread.id} className="group flex items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(thread.id)}
                aria-current={isActive ? "page" : undefined}
                className={`min-w-0 flex-1 rounded-lg px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 ${
                  isActive ? "bg-[var(--brand-primary-soft)]" : "hover:bg-gray-50"
                }`}
              >
                <span className="block truncate text-sm font-medium text-[#131B2A]">
                  {thread.title || "Untitled chat"}
                </span>
                <span className="block text-[11px] text-gray-400">{dayjs(thread.updatedAt).format("D MMM YYYY, h:mm A")}</span>
              </button>
              <Popconfirm
                title="Delete this chat?"
                description="Its draft is removed too. Trips you already saved are not affected."
                okText="Delete"
                okButtonProps={{ danger: true, loading: remove.isPending }}
                onConfirm={async () => {
                  try {
                    await remove.mutateAsync(thread.id);
                    onDeleted(thread.id);
                  } catch {
                    toast.error("Unable to delete this chat");
                  }
                }}
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={`Delete chat ${thread.title || "Untitled chat"}`}
                  icon={<Trash2Icon size={14} />}
                  className="!text-gray-400 hover:!text-red-600"
                />
              </Popconfirm>
            </li>
          );
        })}
      </ul>
    </Drawer>
  );
};

/** Chat-driven itinerary creation: the agent describes a trip and refines a live, priced draft. */
const PortalAssistantPage = () => {
  const paths = getPortalPaths();
  const navigate = useNavigate();
  const location = useLocation();
  const { threadId: routeThreadId } = useParams();
  const queryClient = useQueryClient();
  const { message: toast } = AntApp.useApp();
  const status = usePortalAiStatusQuery();
  const saveDraft = useSavePortalItineraryDraftMutation();

  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState<AssistantDraft | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [progress, setProgress] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingThread, setLoadingThread] = useState(Boolean(routeThreadId));
  const [mobileTab, setMobileTab] = useState<"chat" | "draft">("chat");
  const [historyOpen, setHistoryOpen] = useState(false);
  const sending = progress !== null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const autoSentRef = useRef(false);
  // Starts empty so a chat opened directly by URL (refresh, shared link) is fetched on mount.
  const activeThreadRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, progress]);

  // Load a chat when the URL points at one we are not already showing; reset on /assistant.
  useEffect(() => {
    if (routeThreadId === activeThreadRef.current) return;
    abortRef.current?.abort();
    setProgress(null);
    setError(null);
    activeThreadRef.current = routeThreadId;
    if (!routeThreadId) {
      setMessages([]);
      setDraft(null);
      setTitle(null);
      setLoadingThread(false);
      return;
    }
    setLoadingThread(true);
    queryClient
      .fetchQuery({
        queryKey: assistantKeys.thread(routeThreadId),
        queryFn: async () =>
          (await portalRequest<AssistantThread>({ url: `/api/agent-portal/ai/threads/${routeThreadId}`, method: "get" })).data,
        staleTime: 0,
      })
      .then((thread) => {
        if (activeThreadRef.current !== routeThreadId) return;
        setMessages(thread.messages);
        setDraft(thread.draft);
        setTitle(thread.title);
      })
      .catch(() => {
        if (activeThreadRef.current === routeThreadId) setError("This chat could not be loaded.");
      })
      .finally(() => {
        if (activeThreadRef.current === routeThreadId) setLoadingThread(false);
      });
  }, [routeThreadId, queryClient]);

  const send = useCallback(
    async (text: string, isRetry = false) => {
      const content = text.trim();
      if (!content || sending) return;
      setError(null);
      setInput("");
      if (!isRetry) setMessages((current) => [...current, { role: "user", content }]);
      setProgress("Thinking...");
      setPreviewImages([]);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const result = await sendPortalAiMessage({
          message: content,
          threadId: activeThreadRef.current,
          signal: controller.signal,
          onStatus: setProgress,
          onPreview: (images) =>
            setPreviewImages((current) => Array.from(new Set([...current, ...images])).slice(-12)),
        });
        setMessages((current) => [...current, { role: "assistant", content: result.message }]);
        if (result.draft) {
          setDraft(result.draft);
        }
        setTitle(result.title);
        if (activeThreadRef.current !== result.threadId) {
          activeThreadRef.current = result.threadId;
          navigate(paths.assistantThread(result.threadId), { replace: true });
        }
        void queryClient.invalidateQueries({ queryKey: assistantKeys.threads() });
      } catch (caught: unknown) {
        if (controller.signal.aborted) return;
        setError(caught instanceof AssistantChatError ? caught.message : "The AI assistant is unavailable right now.");
      } finally {
        if (abortRef.current === controller) {
          setProgress(null);
          setPreviewImages([]);
        }
      }
    },
    [navigate, paths, queryClient, sending],
  );

  // A brief typed on the dashboard arrives as navigation state and is sent once.
  useEffect(() => {
    const brief = (location.state as { brief?: string } | null)?.brief;
    if (!brief || autoSentRef.current || routeThreadId || status.isLoading || !status.data?.available) return;
    autoSentRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
    void send(brief);
  }, [location.pathname, location.state, navigate, routeThreadId, send, status.data?.available, status.isLoading]);

  const startNewChat = () => {
    if (!routeThreadId && !messages.length) return;
    navigate(paths.assistant);
  };

  const saveAsTrip = async () => {
    if (!draft) return;
    try {
      const saved = await saveDraft.mutateAsync({ guestName: draft.guestName || undefined, payload: draft.payload });
      toast.success("Trip saved. You can keep editing it here.");
      navigate(paths.editItinerary(saved.id));
    } catch (caught: unknown) {
      const apiError = (caught as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(apiError || "Unable to save this trip");
    }
  };

  const lastMessage = messages[messages.length - 1];
  const needsResume = !sending && lastMessage?.role === "assistant" && lastMessage.content.startsWith("I ran out of steps");
  const followUps = useMemo(
    () => (needsResume ? ["Continue"] : draft && !sending ? FOLLOW_UPS : []),
    [draft, needsResume, sending],
  );

  if (status.isLoading) {
    return (
      <div className="p-6">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (status.isError || !status.data?.available) {
    return (
      <div className="grid min-h-[60dvh] place-items-center p-6">
        <Result
          icon={<SparklesIcon className="mx-auto text-gray-400" size={40} />}
          title="AI assistant unavailable"
          subTitle={status.data?.reason || "We couldn't check whether the AI assistant is available. Please try again."}
          extra={
            <Link to={paths.newRequest}>
              <Button type="primary">Build a trip manually</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const chatPane = (
    <section
      aria-label="AI assistant chat"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
    >
      <header className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            AI assistant · uses live inventory
          </p>
          <h1 className="truncate text-base font-semibold text-[#131B2A]">{title || "New trip chat"}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Tooltip title="Chat history">
            <Button aria-label="Chat history" icon={<HistoryIcon size={15} />} onClick={() => setHistoryOpen(true)} />
          </Tooltip>
          <Button icon={<PlusIcon size={14} />} onClick={startNewChat} disabled={sending}>
            New chat
          </Button>
        </div>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5" aria-live="polite">
        {loadingThread ? <Skeleton active paragraph={{ rows: 5 }} /> : null}
        {!loadingThread && !messages.length ? (
          <div className="mx-auto flex max-w-xl flex-col items-center py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
              <SparklesIcon size={22} />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-[#131B2A]">Tell me what your client wants</h2>
            <p className="mt-1 text-sm text-gray-500">
              Share destinations, nights, travellers and a start date. I&apos;ll build a priced itinerary you can refine.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setInput(suggestion)}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-[#131B2A] transition-colors hover:border-(--brand-primary)/50 hover:bg-[var(--brand-primary-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {messages.map((entry, index) =>
          entry.role === "user" ? (
            <div key={index} className="flex justify-end">
              <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-(--brand-secondary) px-4 py-2.5 text-sm leading-6 text-white">
                {entry.content}
              </p>
            </div>
          ) : (
            <div key={index} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
                <SparklesIcon size={15} />
              </span>
              <div className="max-w-[90%] rounded-2xl rounded-tl-md border border-gray-200 bg-[#FBFBFB] px-4 py-3 text-[#131B2A]">
                <MessageText text={entry.content} />
              </div>
            </div>
          ),
        )}

        {sending ? (
          <div className="flex items-center gap-3" role="status">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
              <SparklesIcon size={15} className="animate-pulse" />
            </span>
            <div className="min-w-0">
              <span className="text-sm text-gray-500">{progress}</span>
              <LoadingImageCarousel images={previewImages} />
            </div>
          </div>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            closable
            onClose={() => setError(null)}
            action={
              messages.length && messages[messages.length - 1].role === "user" ? (
                <Button size="small" onClick={() => void send(messages[messages.length - 1].content, true)}>
                  Retry
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </div>

      <div className="shrink-0 space-y-2 border-t border-gray-200 px-4 py-3 sm:px-5">
        {followUps.length ? (
          <div className="flex flex-wrap gap-2">
            {followUps.map((followUp) => (
              <button
                key={followUp}
                type="button"
                onClick={() => void send(followUp)}
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-[#131B2A] transition-colors hover:border-(--brand-primary)/50 hover:bg-[var(--brand-primary-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50"
              >
                {followUp}
              </button>
            ))}
          </div>
        ) : null}
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void send(input);
          }}
        >
          <Input.TextArea
            aria-label="Message the AI assistant"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onPressEnter={(event) => {
              if (event.shiftKey) return;
              event.preventDefault();
              void send(input);
            }}
            autoSize={{ minRows: 1, maxRows: 5 }}
            maxLength={4000}
            placeholder="Describe the trip, or ask to change hotels, dates, budget…"
            className="!rounded-lg"
          />
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            aria-label="Send message"
            icon={<SendIcon size={16} />}
            loading={sending}
            disabled={!input.trim()}
          />
        </form>
        <p className="mt-2 text-center text-[11px] leading-4 text-gray-500">
          AI-generated itineraries and prices can be inaccurate. Review every item and the total before sharing a quote.
        </p>
      </div>
    </section>
  );

  const draftPane = (
    <aside
      aria-label="Itinerary draft"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)] lg:w-[400px] lg:flex-none xl:w-[440px]"
    >
      <DraftPanel
        draft={draft}
        busy={sending}
        saving={saveDraft.isPending}
        onSave={() => void saveAsTrip()}
        onFix={(prompt) => {
          setMobileTab("chat");
          void send(prompt);
        }}
      />
    </aside>
  );

  return (
    <div className="flex h-[calc(100dvh-5rem)] flex-col gap-3 px-4 pt-4 sm:px-6 lg:h-[calc(100dvh-1.5rem)] lg:px-8">
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 lg:hidden" role="tablist" aria-label="Assistant view">
        {(["chat", "draft"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={mobileTab === tab}
            onClick={() => setMobileTab(tab)}
            className={`min-h-9 flex-1 rounded-md text-sm font-medium transition-colors ${
              mobileTab === tab ? "bg-white text-[#131B2A] shadow-sm" : "text-gray-500"
            }`}
          >
            {tab === "chat" ? "Chat" : draft ? "Draft ●" : "Draft"}
          </button>
        ))}
      </div>
      <div className="flex min-h-0 flex-1 gap-4">
        <div className={`${mobileTab === "chat" ? "flex" : "hidden"} min-h-0 min-w-0 flex-1 lg:flex`}>{chatPane}</div>
        <div className={`${mobileTab === "draft" ? "flex" : "hidden"} min-h-0 min-w-0 flex-1 lg:flex lg:flex-none`}>
          {draftPane}
        </div>
      </div>
      <ChatHistoryDrawer
        open={historyOpen}
        activeThreadId={routeThreadId}
        onClose={() => setHistoryOpen(false)}
        onSelect={(id) => {
          setHistoryOpen(false);
          if (id !== routeThreadId) navigate(paths.assistantThread(id));
        }}
        onDeleted={(id) => {
          if (id === routeThreadId) {
            setHistoryOpen(false);
            navigate(paths.assistant);
          }
        }}
      />
    </div>
  );
};

export default PortalAssistantPage;
